import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LockIcon from '@mui/icons-material/Lock';
import {
  Box, TextField, Theme, Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import React, { useEffect } from 'react';
import * as yup from 'yup';
import ETOButton from '../../../Button/ETOButton';

type InitialLoginValues = {
  userName: string;
  password: string;
};

const form = (theme: Theme) => ({
  alignItems: 'center',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  position: 'relative',
  width: '100%',
  [theme.breakpoints.down('sm')]: {
    width: theme.spacing(36),
  },
  [theme.breakpoints.up('sm')]: {
    width: theme.spacing(40),
  },
});

const textInput = (theme: Theme) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(3),
  position: 'relative',
  '& .MuiOutlinedInput-input': {
    paddingLeft: theme.spacing(1),
  },
});
const helperText = (theme: Theme) => ({
  position: 'absolute',
  top: theme.spacing(7),
});

const errorTextSx = (theme: Theme) => ({
  color: theme.palette.error.main,
  paddingTop: theme.spacing(2),
});

const initialValues = {
  userName: '',
  password: '',
  rememberCheck: false,
};

const validationSchema = yup.object({
  userName: yup.string().required('Username is required'),
  password: yup.string().required('Password is required'),
  rememberCheck: yup.boolean(),
});

interface LoginFormProps {
  /* eslint-disable */
  onLogin: (
    userName: string,
    password: string,
  ) => Promise<void>;
  /* eslint-enable */
  validationError?: {
    userName?: string;
    password?: string;
  };
  captions?: {
    userName?: string;
    password?: string;
    signIn?: string;
  };
  error?: string;
}

const LoginForm: React.FC<React.PropsWithChildren<LoginFormProps>> = (props) => {
  // const classes = useStyles();
  const {
    error, onLogin, validationError, captions,
  } = props;

  const formik = useFormik<InitialLoginValues>({
    initialValues: {
      userName: initialValues?.userName ?? '',
      password: initialValues?.password ?? '',
    },
    validationSchema,
    onSubmit: (values, actions) => {
      onLogin(
        values.userName,
        values.password,
      )
        .then(() => {
          actions.setSubmitting(false);
        })
        .catch((e) => {
          actions.setSubmitting(false);

          if (Object.prototype.hasOwnProperty.call(e, 'errors')) {
            actions.setErrors(e.errors);
          } else {
            throw e;
          }
        });
    },
  });

  useEffect(() => {
    if (validationError) {
      formik.setErrors(validationError);
    }
  }, [validationError]);

  return (
    <Box component="form" sx={form} onSubmit={formik.handleSubmit}>
      <TextField
        variant="outlined"
        id="login-userName"
        name="userName"
        label={captions?.userName ?? 'Username'}
        sx={textInput}
        onChange={formik.handleChange}
        fullWidth
        InputProps={{
          startAdornment: (
            <AccountCircleIcon
              color={`${
                formik.touched.userName && Boolean(formik.errors.userName)
                  ? 'error'
                  : 'primary'
              }`}
            />
          ),
        }}
        value={formik.values.userName}
        error={formik.touched.userName && Boolean(formik.errors.userName)}
        FormHelperTextProps={{ classes: helperText }}
        helperText={formik.touched.userName && formik.errors.userName}
      />

      <TextField
        variant="outlined"
        id="login-password"
        type="password"
        name="password"
        label={captions?.password ?? 'Password'}
        sx={textInput}
        onChange={formik.handleChange}
        fullWidth
        InputProps={{
          startAdornment: (
            <LockIcon
              color={`${
                formik.touched.password && Boolean(formik.errors.password)
                  ? 'error'
                  : 'primary'
              }`}
            />
          ),
        }}
        value={formik.values.password}
        error={formik.touched.password && Boolean(formik.errors.password)}
        FormHelperTextProps={{ classes: helperText }}
        helperText={formik.touched.password && formik.errors.password}
      />

      <ETOButton
        size="medium"
        buttonProps={{
          variant: 'contained',
          fullWidth: true,
        }}
        color="primary"
        loading={formik.isSubmitting}
        type="submit"
      >
        {captions?.signIn ?? 'Sign In'}
      </ETOButton>

      {
        error && (
          <Typography sx={errorTextSx} variant="caption">
            {error}
          </Typography>
        )
      }
    </Box>
  );
};

export default LoginForm;
